// ─── src/services/weddingReasoning.js ────────────────────────────────────────
// The Wedding Reasoning Engine, judges each candidate against THIS wedding and
// returns a structured, storable evidence object.
//
// Scores are an internal detail; reasoning is the product. Four questions:
//   1. Can they do this wedding?   (eligibility, hard gate)
//   2. Do they fit this wedding?   (style + scale)
//   3. Can Aura explain it?        (>= 1 grounded reason, else DO NOT recommend)
//   4. Relationship intelligence?  (unknown for now, the graph fills it later)
//
// Golden rule: if Aura cannot explain it, Aura does not recommend it.
// Positive reasons face the couple; concerns (negative) are stored as evidence.

const norm = (s) => String(s || "").toLowerCase().trim();

// Destination → canonical country, covering every country LWD actually lists (plus
// the destination + DB country strings that resolve to each). Used to catch a
// clearly-wrong country; never to fabricate a match. Home nations resolve to "uk" so
// a UK venue is never wrongly rejected for a London wedding, and vice versa.
const DEST_COUNTRY = {
  // Italy
  "amalfi": "italy", "amalfi coast": "italy", "ravello": "italy", "positano": "italy",
  "tuscany": "italy", "florence": "italy", "siena": "italy", "umbria": "italy",
  "lake como": "italy", "como": "italy", "lombardy": "italy", "sicily": "italy",
  "puglia": "italy", "rome": "italy", "lazio": "italy", "venice": "italy", "veneto": "italy",
  "campania": "italy", "italy": "italy",
  // France
  "france": "france", "provence": "france", "paris": "france", "french riviera": "france", "cote d'azur": "france", "loire": "france",
  // Iberia
  "spain": "spain", "mallorca": "spain", "majorca": "spain", "marbella": "spain", "barcelona": "spain", "ibiza": "spain",
  "portugal": "portugal", "lisbon": "portugal", "algarve": "portugal",
  // UK + Ireland (every home nation → "uk" for country-match)
  "uk": "uk", "u.k.": "uk", "united kingdom": "uk", "britain": "uk", "great britain": "uk",
  "england": "uk", "london": "uk", "oxfordshire": "uk", "cotswolds": "uk", "cotswold": "uk",
  "surrey": "uk", "buckinghamshire": "uk", "hampshire": "uk", "kent": "uk", "yorkshire": "uk",
  "lake district": "uk", "scotland": "uk", "edinburgh": "uk", "scottish highlands": "uk",
  "highlands": "uk", "wales": "uk",
  "ireland": "ireland", "dublin": "ireland",
  // Rest of LWD's coverage
  "greece": "greece", "santorini": "greece", "mykonos": "greece",
  "hungary": "hungary", "budapest": "hungary",
  "switzerland": "switzerland",
  "uae": "uae", "dubai": "uae", "abu dhabi": "uae",
  "qatar": "qatar", "doha": "qatar",
  "romania": "romania", "slovenia": "slovenia",
  "usa": "usa", "united states": "usa", "america": "usa",
  "maldives": "maldives", "bali": "indonesia", "indonesia": "indonesia",
};
export function countryOf(text) {
  const t = norm(text);
  if (!t) return null;
  for (const [k, v] of Object.entries(DEST_COUNTRY)) if (t.includes(k)) return v;
  return null;
}

// couple-facing phrasing for a positive reason.
//
// ctx.destination, the ACTIVE wedding's destination. A stored reason bakes in the
// location it was created for; once the wedding moves (the couple explored
// elsewhere), a location claim like "In London, where you're marrying" becomes
// false. When ctx says the active wedding no longer matches, we NEVER assert the
// stale location, we degrade to an honest "previously considered" line, or omit
// the reason (return null → filtered out). No context passed = original behaviour,
// which is correct for a fresh candidate reasoned against the current wedding.
// This is the interim honesty guard ahead of Wedding Scenarios; see
// _WEDDING_SCENARIOS_SCOPE.md.
export function reasonPhrase(r, ctx) {
  const active = ctx && ctx.destination ? String(ctx.destination) : null;
  const sameCountry = (a, b) => { const ca = countryOf(a), cb = countryOf(b); return !!ca && ca === cb; };
  const locMatches = (loc) => {
    const nd = norm(active), nl = norm(loc);
    return !!nd && !!nl && (nd.includes(nl) || nl.includes(nd) || sameCountry(active, loc));
  };
  switch (r.type) {
    case "destination": {
      const loc = r.evidence;
      if (active && loc && !locMatches(loc)) {
        return loc ? `Previously considered for your ${loc} wedding` : null;
      }
      return `In ${r.evidence}, where you're marrying`;
    }
    case "country": {
      // "Works across Italy" is stale once the wedding leaves that country, omit
      // it rather than mislead.
      if (active && r.evidence && !locMatches(r.evidence)) return null;
      return `Works across ${cap(r.evidence)}`;
    }
    case "style":       return `Matches your ${r.evidence} style`;
    case "scale":       return `Sized for ${r.evidence}`;
    case "editorial":   return `Editorially recognised on LWD`;
    default:            return r.evidence;
  }
}
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Judge one candidate against the wedding. Returns the full evidence object.
export function reasonAboutCandidate(wedding, item, opts = {}) {
  const role = opts.role || item.role || "vendor";
  const reasons = [];   // positive → the couple
  const concerns = [];  // negative → stored as evidence, never shown
  const checks = [];    // {weight, points} → earns the confidence
  let eligible = true;

  const dest = norm(wedding.destination);
  const cRegion = norm(item.region), cCountry = norm(item.country), cCity = norm(item.city);
  const wCountry = opts.weddingCountry || countryOf(wedding.destination);
  // Resolve the candidate's country through the SAME table so "United Kingdom" and
  // "England" both become "uk" and compare correctly against the wedding's country.
  const candCountry = countryOf(item.country) || countryOf(item.region) || cCountry;

  // ── Q1: geography ────────────────────────────────────────────────────────
  const regionMatch = dest && cRegion && (dest.includes(cRegion) || cRegion.includes(dest));
  const cityMatch   = dest && cCity && dest.includes(cCity);
  if (regionMatch || cityMatch) {
    reasons.push({ type: "destination", evidence: item.region || item.city || wedding.destination, confidence: 100 });
    checks.push({ weight: 0.35, points: 0.35 });
  } else if (wCountry && candCountry && wCountry === candCountry) {
    // same country, different region, a real (if softer) reason: works across the
    // country. Grounded and defensible, so the candidate can surface; partial credit
    // keeps confidence honestly below an exact-region match, and we still log the
    // "not local" concern as evidence behind the scenes.
    reasons.push({ type: "country", evidence: wCountry, confidence: 70 });
    checks.push({ weight: 0.35, points: 0.18 });
    if (cRegion && !regionMatch) concerns.push({ type: "destination", evidence: `based in ${item.region || item.country}, not ${wedding.destination}` });
  } else if (wCountry && candCountry && wCountry !== candCountry) {
    // clearly a different country, cannot do this wedding
    eligible = false;
    checks.push({ weight: 0.35, points: 0 });
    concerns.push({ type: "destination", evidence: `based in ${item.country}, wedding is in ${cap(wCountry)}` });
  } else {
    checks.push({ weight: 0.35, points: 0 });
  }

  // ── Q2: fit, style ──────────────────────────────────────────────────────
  const wStyle = norm(wedding.style);
  if (wStyle) {
    const tags = (item.styles || item.specialties || []).map(norm);
    const sem = item.scores?.semanticMatch ?? 0;
    const styleHit = tags.some((t) => t && (t.includes(wStyle) || wStyle.includes(t))) || sem >= 70;
    if (styleHit) {
      reasons.push({ type: "style", evidence: wedding.style, confidence: Math.max(70, Math.round(sem)) });
      checks.push({ weight: 0.30, points: 0.30 });
    } else {
      checks.push({ weight: 0.30, points: 0 });
      if (sem > 0 && sem < 45) concerns.push({ type: "style", evidence: `weaker alignment with ${wedding.style}` });
    }
  }

  // ── Q2: fit, scale (venues only) ────────────────────────────────────────
  if (role === "venue" && wedding.guest_count) {
    const cap0 = Number(item.capacity) || 0;
    if (cap0 && cap0 >= wedding.guest_count) {
      reasons.push({ type: "scale", evidence: `${wedding.guest_count} guests`, confidence: 90 });
      checks.push({ weight: 0.20, points: 0.20 });
    } else if (cap0 && cap0 < wedding.guest_count) {
      checks.push({ weight: 0.20, points: 0 });
      concerns.push({ type: "scale", evidence: `holds ${cap0}, you need ${wedding.guest_count}` });
      if (cap0 < wedding.guest_count * 0.8) eligible = false; // genuinely can't fit
    }
  }

  // ── objective editorial signal ───────────────────────────────────────────
  if ((item.scores?.editorialQuality ?? 0) >= 60) {
    reasons.push({ type: "editorial", evidence: "LWD editorial", confidence: 80 });
    checks.push({ weight: 0.15, points: 0.15 });
  }

  // ── Q3: explainability ───────────────────────────────────────────────────
  const explainable = reasons.length >= 1;

  // ── confidence = evidence coverage over applicable checks, tempered by concerns
  const possible = checks.reduce((a, c) => a + c.weight, 0) || 1;
  const earned = checks.reduce((a, c) => a + c.points, 0);
  let confidence = Math.round((earned / possible) * 100);
  confidence = Math.max(0, Math.min(100, confidence - concerns.length * 5));

  return {
    entity_id: item.id,
    entity_name: item.name,
    role,
    eligible,
    explainable,
    recommend: eligible && explainable,          // the golden rule, encoded
    confidence,
    reasons,                                      // couple-facing (positive)
    concerns,                                     // evidence-only (negative)
    relationship: "unknown",                      // Q4, the graph fills this later
  };
}

// The best EXPLAINABLE candidate for a role (or null, golden rule: better none
// than a weak one). Returns { item, reasoning } | null.
export function bestCandidate(wedding, items, role, weddingCountry) {
  const judged = (items || [])
    .map((it) => ({ item: it, reasoning: reasonAboutCandidate(wedding, it, { role, weddingCountry }) }))
    .filter((x) => x.reasoning.recommend)
    .sort((a, b) => b.reasoning.confidence - a.reasoning.confidence);
  return judged[0] || null;
}
